import * as React from "react";
import { graphql } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import { Media } from "../components/Media";
import Seo from "../components/Seo";
import AussieOwned from "../components/seals/AustralianOwned";
import Vegan from "../components/seals/VeganFriendly";
import GlutenFree from "../components/seals/GlutenFree";
import OneADay from "../components/seals/OneADay";
import StickyWrapper from "../components/StickyWrapper";
import RockyTransition from "../components/bActive/RockyTransition";
import CustomerQuote from "../components/CustomerQuote";
import Metafolin from "../components/bActive/Metafolin";
import ogImage from "../images/bActive/b-active-bottle-with-runner.jpg";
import "../styles/bActive.css";

const BActive = ({ data }) => {
  const product = getImage(data.product);
  const products = getImage(data.products);
  const hiker = getImage(data.hiker);
  const capsule = getImage(data.capsule);
  const happyGuy = getImage(data.happyGuy);
  const battleRopes = getImage(data.battleRopes);

  return (
    <>
      <Seo
        title="B-Active™: Activated B-Vitamin Complex"
        description="B-Active™ provides the full suite of all eight B-vitamins with added Vitamin C and CoQ10 for energy production and antioxidant support. Made in Australia."
        image={`https://www.ithrivenutrition.com.au${ogImage}`}
        alt="B-Active bottle in front of a man running."
        handle="b-active"
      />
      <div className="bg-gray-darker">
        <div className="max-w-screen-lg m-auto pt-2 md:pt-6 lg:pt-10">
          <StickyWrapper
            title="B-Active"
            subtitle="Activated B-Vitamin Complex To Support Energy Production"
            shopLink="b-active"
          />
          <div className="flex flex-wrap-reverse md:flex-nowrap">
            <div className="text-white mt-0 md:mt-10 w-full px-6 md:p-0 md:w-5/12">
              <p>
                <strong>B-Active™</strong> takes the best of modern science to
                maintain and support:
              </p>
              <ul className="list-inside mt-2">
                <li className="list-circle white">
                  Energy production and vitality
                </li>
                <li className="list-circle white">Nervous system function</li>
                <li className="list-circle white">Healthy stress response</li>
                <li className="list-circle white">Cognitive function</li>
                <li className="list-circle white">Cardiovascular health</li>
                <li className="list-circle white">
                  General health and well being
                </li>
              </ul>

              <div className="flex justify-center my-10">
                <AussieOwned fill="#2BAE76" />
                <Vegan fill="#2BAE76" />
                <GlutenFree fill="#2BAE76" />
                <OneADay fill="#2BAE76" />
              </div>
            </div>
            <div className="w-full text-right -mb-8 md:mb-0 md:w-7/12 relative">
              <Media lessThan="md">
                <GatsbyImage
                  image={product}
                  alt="You Beauty bottle"
                  className="bottle-hero"
                />
              </Media>
              <Media greaterThanOrEqual="md">
                <GatsbyImage
                  image={products}
                  alt="You Beauty bottles"
                  className="bottles-hero"
                  style={{ position: "absolute" }}
                />
              </Media>
            </div>
          </div>
        </div>
      </div>
      <div className="green-gradient"></div>
      <div className="relative overflow-hidden">
        <Media lessThan="md" className="absolute z-10 right-0 left-0">
          <h2 className="text-35 text-center text-blue-dark p-8 pb-16 -mb-16">
            Fuel Your <span style={{ color: "#C85F24" }}>Fire.</span>
          </h2>
        </Media>
        <div className="bActiveHero md:h-full md:w-full 2xl:mx-auto md:relative md:-mb-6">
          <BgImage
            image={hiker}
            style={{ height: "100%", backgroundPosition: "0% 75%" }}
          />
          <Media greaterThanOrEqual="md">
            <div className="absolute right-0 top-0 bottom-0 flex items-center 2xl:left-1/2">
              <div className="bg-white bg-opacity-70 py-12 md:px-12 xl:px-24">
                <h2 className="text-50 text-center mb-4 text-blue-dark">
                  Fuel Your <span style={{ color: "#C85F24" }}>Fire.</span>
                </h2>
                <p className="max-w-prose mx-auto leading-loose relative mb-3">
                  B vitamins play an important role in maintaining health,
                  enhancing performance, and ensuring that energy can be
                  produced for physical activity.
                </p>
              </div>
            </div>
          </Media>
        </div>
        <RockyTransition />
        <Media lessThan="md" className="md:mb-12">
          <div className="bg-white p-8 pb-20">
            <p className="max-w-prose mx-auto leading-loose relative mb-3">
              B vitamins play an important role in maintaining health, enhancing
              performance, and ensuring that energy can be produced for physical
              activity.
            </p>
          </div>
        </Media>
        <RockyTransition green={true} height="70" />
        <div className="grid grid-cols-1 md:grid-cols-2 md:shadow-md capsuleGradient">
          <div className="text-white self-center 2xl:justify-self-end 2xl:mr-32">
            <h2 className="text-35 md:text-45 text-center leading-normal pt-6 md:pt-12 relative mb-6">
              <span style={{ color: "#9CFFD5" }}>One For All,</span>
              <br />
              All in One.
            </h2>
            <GatsbyImage
              image={capsule}
              alt="Close up of B Active capsule"
              className="my-6 max-w-xs mx-auto block"
            />
            <p className="px-8 pb-8 leading-loose max-w-md mx-auto md:text-18 md:leading-loose">
              <strong>B-Active™</strong> provides the full suite of all 8
              B&nbsp;vitamins with added Vitamin C and CoQ10 for energy
              production and antioxidant support.
            </p>
          </div>
          <GatsbyImage
            image={happyGuy}
            alt="Active man smiling after workout"
            className="p-14 xl:max-w-2xl"
          />
        </div>
        <Media lessThan="md">
          <div className="green-gradient"></div>
        </Media>
        <div className="md:grid md:grid-cols-2 md:relative">
          <div className="navyGradient text-white md:order-2">
            <div className="2xl:max-w-screen-sm 2xl:ml-16">
              <h2 className="text-50 text-center pt-12 mb-2">
                B <span style={{ color: "#FF3A3A" }}>&ne;</span> B
              </h2>
              <h3 className="text-25 text-center">
                Not all B Vitamins are Equal
              </h3>
              <p className="p-8 pb-4 leading-loose">
                Active forms of B vitamins are ready for the body to use
                immediately. <strong>B-Active™</strong> is formulated with
                active forms of all B&nbsp;vitamins and contains the naturally
                occurring primary form of folate <strong>Metafolin®</strong>.
              </p>
              <Metafolin light={true} />
              <p className="p-8 leading-loose">
                When you exercise frequently and at a high intensity, your cells
                require more B-vitamins to replenish your energy. Taking them in
                an activated form helps ensure that they are delivered straight
                to your cells so they can help optimise energy production.
              </p>
            </div>
          </div>
          <div className="battleGradient absolute h-12 w-full z-10 md:h-full md:w-40 md:right-1/2"></div>
          <div className="h-96 md:h-full">
            <BgImage
              image={battleRopes}
              style={{ height: "100%", backgroundPosition: "80% 80%" }}
            />
          </div>
        </div>
      </div>
      <div className="bg-gray-dark p-6">
        <h2 className="text-35 md:text-40 text-white text-center">
          Results That Speak For Themselves.
        </h2>
        <div className="flex flex-col md:flex-row md:justify-center items-center">
          <CustomerQuote name="Leanne" color="green" isRight={true}>
            <p>
              BEST Bs EVER!!!
              <br />I have always taken a B vitamin and when I tried the
              B-Active I felt a lot better, a lot faster. It is the best I have
              tried and am now a devoted fan! :&#41; Thank you for making such a
              wonderful product :&#41;
            </p>
          </CustomerQuote>
        </div>
      </div>
    </>
  );
};

export default BActive;

export const query = graphql`
  query {
    product: file(relativePath: { eq: "bActive/b-active-bottle.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    products: file(relativePath: { eq: "bActive/b-active-bottles.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    hiker: file(relativePath: { eq: "bActive/sunrise-hiker-on-mountain.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    capsule: file(relativePath: { eq: "bActive/b-active-close-up.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    happyGuy: file(relativePath: { eq: "bActive/man-exercising-outside.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    battleRopes: file(relativePath: { eq: "bActive/beach-battle-ropes.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
  }
`;
